import React, { useState, useEffect } from 'react';
import { Pencil, Save, ExternalLink, Calendar, FileText, MessageSquare, Megaphone, StickyNote, Link2, ChevronUp } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { cleanNote } from '../utils/cleanNote';

export interface PublicContenuto {
  id: string;
  formatoPost?: 'grafica' | 'foto';
  titolo: string;
  descrizione?: string;
  body?: string;
  hook?: string;
  cta?: string;
  stato: string;
  dataPubblicazione?: string | Date;
  note?: string;
  linkIspirazione?: string;
  copertinaUrl?: string;
  copyPost?: string;
  /** post = post grafico (solo copy + immagine), reel = video */
  tipo?: 'reel' | 'post';
}

interface Props {
  content: PublicContenuto;
  clientId: string;
  onContentUpdated: (updatedContent: PublicContenuto) => void;
  getContentStyle: (stato?: string) => { dot: string; label: string };
  /** Chiude il pannello (torna all'anteprima nella lista). */
  onClose: () => void;
}

/**
 * Scheda completa di un contenuto, con modifica in linea.
 *
 * Vive DENTRO la pagina del cliente, non in un popup: scrivere e correggere un
 * testo è l'attività principale di chi apre questa pagina, e su telefono una
 * finestra modale con dentro delle textarea è scomoda (tastiera che copre il
 * campo, scroll che si blocca, chiusura accidentale toccando fuori).
 */
const ClientContentEditor: React.FC<Props> = ({
  content,
  clientId,
  onContentUpdated,
  getContentStyle,
  onClose,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    hook: '',
    body: '',
    cta: '',
    post_copy: '',
    notes: '',
  });
  // Post grafico: al posto di hook/corpo/CTA ci sono il copy e l'immagine
  const isPostGrafico = content.tipo === 'post';

  useEffect(() => {
    setFormData({
      title: content.titolo || '',
      hook: content.hook || '',
      body: content.descrizione || content.body || '',
      cta: content.cta || '',
      post_copy: content.copyPost || '',
      notes: cleanNote(content.note) || '',
    });
    setIsEditing(false);
  }, [content]);

  const hasRealText = (text?: string): boolean => {
    if (!text) return false;
    const trimmed = text.trim();
    return trimmed.length > 0 && trimmed !== '-' && trimmed !== '.';
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`/api/clients/${clientId}/contents/${content.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await res.json();
      if (data.success) {
        onContentUpdated({
          ...content,
          titolo: formData.title,
          hook: formData.hook || undefined,
          descrizione: formData.body || undefined,
          body: formData.body || undefined,
          cta: formData.cta || undefined,
          copyPost: formData.post_copy || undefined,
          note: formData.notes || undefined,
        });
        setIsEditing(false);
        toast.success('Contenuto aggiornato!');
      } else {
        toast.error(data.message || 'Errore nel salvataggio');
      }
    } catch {
      toast.error('Errore di connessione');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      title: content.titolo || '',
      hook: content.hook || '',
      body: content.descrizione || content.body || '',
      cta: content.cta || '',
      post_copy: content.copyPost || '',
      notes: cleanNote(content.note) || '',
    });
    setIsEditing(false);
  };

  const contentStyle = getContentStyle(content.stato);
  const cleanedNote = cleanNote(content.note);

  const campoClasse =
    'w-full text-sm text-slate-700 dark:text-slate-200 bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-300 dark:border-yellow-700 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-yellow-400 leading-relaxed';

  return (
    <div className="space-y-4">
      {/* Stato + data */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 dark:bg-gray-700 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-gray-600">
          <span className={`w-2 h-2 rounded-full ${contentStyle.dot}`}></span>
          {contentStyle.label}
        </span>
        {content.dataPubblicazione && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-800">
            <Calendar className="w-3 h-3" />
            {format(new Date(content.dataPubblicazione), 'dd MMMM yyyy', { locale: it })}
          </span>
        )}
      </div>

      {/* Post grafico: immagine + copy (modificabile) */}
      {isPostGrafico && (
      <div className="rounded-xl border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 p-3 sm:p-4 flex flex-col sm:flex-row gap-3 sm:gap-4">
        {content.copertinaUrl ? (
          <img src={content.copertinaUrl} alt="" className="w-32 h-40 rounded-lg object-cover flex-shrink-0 border border-purple-200 dark:border-purple-800" />
        ) : (
          <div className="w-32 h-40 rounded-lg flex-shrink-0 border border-dashed border-purple-300 dark:border-purple-700 flex items-center justify-center text-xs text-purple-400 text-center px-2">Immagine in arrivo</div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <FileText className="w-3.5 h-3.5 text-purple-500" />
            <span className="text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wide">
              {content.formatoPost === 'foto' ? 'Carosello foto' : 'Post grafico'} · Copy
            </span>
            {isEditing && <Pencil className="w-3 h-3 text-yellow-500" />}
          </div>
          {isEditing ? (
            <textarea
              value={formData.post_copy}
              onChange={(e) => setFormData((prev) => ({ ...prev, post_copy: e.target.value }))}
              rows={8}
              className={`${campoClasse} resize-y`}
              placeholder="Scrivi il copy del post..."
            />
          ) : hasRealText(content.copyPost) ? (
            <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap leading-relaxed">
              {content.copyPost}
            </p>
          ) : (
            <p className="text-sm text-slate-400 dark:text-slate-500 italic">Copy da scrivere</p>
          )}
        </div>
      </div>
      )}

      {/* Script (reel) */}
      {!isPostGrafico && (
      <div className="rounded-xl border border-slate-200 dark:border-gray-700 overflow-hidden divide-y divide-slate-200 dark:divide-gray-700">
        {/* Hook */}
        <div className="p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-1.5">
            <MessageSquare className="w-3.5 h-3.5 text-amber-500" />
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              Hook
            </span>
            {isEditing && <Pencil className="w-3 h-3 text-yellow-500" />}
          </div>
          {isEditing ? (
            <textarea
              value={formData.hook}
              onChange={(e) => setFormData((prev) => ({ ...prev, hook: e.target.value }))}
              rows={2}
              className={`${campoClasse} resize-y`}
              placeholder="Scrivi l'hook..."
            />
          ) : hasRealText(content.hook) ? (
            <p className="text-sm text-slate-800 dark:text-slate-200 leading-relaxed font-medium whitespace-pre-wrap">
              {content.hook}
            </p>
          ) : (
            <p className="text-sm text-slate-400 dark:text-slate-500 italic">Nessun hook</p>
          )}
        </div>

        {/* Corpo */}
        <div className="p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-1.5">
            <FileText className="w-3.5 h-3.5 text-blue-500" />
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              Corpo
            </span>
            {isEditing && <Pencil className="w-3 h-3 text-yellow-500" />}
          </div>
          {isEditing ? (
            <textarea
              value={formData.body}
              onChange={(e) => setFormData((prev) => ({ ...prev, body: e.target.value }))}
              rows={8}
              className={`${campoClasse} resize-y`}
              placeholder="Scrivi il corpo del contenuto..."
            />
          ) : hasRealText(content.descrizione || content.body) ? (
            <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap leading-relaxed">
              {content.descrizione || content.body}
            </p>
          ) : (
            <p className="text-sm text-slate-400 dark:text-slate-500 italic">Nessun corpo</p>
          )}
        </div>

        {/* CTA */}
        <div className="p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-1.5">
            <Megaphone className="w-3.5 h-3.5 text-green-500" />
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              Call to Action
            </span>
            {isEditing && <Pencil className="w-3 h-3 text-yellow-500" />}
          </div>
          {isEditing ? (
            <input
              type="text"
              value={formData.cta}
              onChange={(e) => setFormData((prev) => ({ ...prev, cta: e.target.value }))}
              className={campoClasse}
              placeholder="Scrivi la CTA..."
            />
          ) : hasRealText(content.cta) ? (
            <p className="text-sm text-slate-700 dark:text-slate-300 font-medium">{content.cta}</p>
          ) : (
            <p className="text-sm text-slate-400 dark:text-slate-500 italic">Nessuna CTA</p>
          )}
        </div>
      </div>
      )}

      {/* Copy del post (caption del reel): modificabile come il resto (29/10/2026,
          il titolare: «anche la caption devi poter modificare dalla pagina del calendario»). */}
      {!isPostGrafico && (isEditing || hasRealText(content.copyPost)) && (
        <div className="rounded-xl border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-1.5">
            <FileText className="w-3.5 h-3.5 text-purple-500" />
            <span className="text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wide">
              Copy del post
            </span>
            {isEditing && <Pencil className="w-3 h-3 text-yellow-500" />}
          </div>
          {isEditing ? (
            <textarea
              value={formData.post_copy}
              onChange={(e) => setFormData((prev) => ({ ...prev, post_copy: e.target.value }))}
              rows={8}
              className={`${campoClasse} resize-y`}
              placeholder="Scrivi il testo che esce sotto il reel..."
            />
          ) : (
            <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap leading-relaxed">
              {content.copyPost}
            </p>
          )}
        </div>
      )}

      {/* Note */}
      <div className="rounded-xl border border-slate-200 dark:border-gray-700 p-3 sm:p-4">
        <div className="flex items-center gap-2 mb-1.5">
          <StickyNote className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
            Note
          </span>
          {isEditing && <Pencil className="w-3 h-3 text-yellow-500" />}
        </div>
        {isEditing ? (
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
            rows={3}
            className={`${campoClasse} resize-y`}
            placeholder="Aggiungi una nota..."
          />
        ) : cleanedNote ? (
          <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed whitespace-pre-wrap">
            {cleanedNote}
          </p>
        ) : (
          <p className="text-sm text-slate-400 dark:text-slate-500 italic">Nessuna nota</p>
        )}
      </div>

      {/* Link di riferimento */}
      {content.linkIspirazione && (
        <a
          href={content.linkIspirazione}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2.5 p-3 sm:p-4 rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors group"
        >
          <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-800 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200 dark:group-hover:bg-blue-700 transition-colors">
            <Link2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Link di riferimento</p>
            <p className="text-xs text-blue-500 dark:text-blue-400 truncate">{content.linkIspirazione}</p>
          </div>
          <ExternalLink className="w-4 h-4 text-blue-400 flex-shrink-0" />
        </a>
      )}

      {/* Azioni: restano in fondo al pannello, dentro la pagina.
          Su mobile occupano tutta la larghezza per essere raggiungibili col pollice. */}
      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-1">
        {isEditing ? (
          <>
            <button
              onClick={handleCancel}
              disabled={saving}
              className="px-4 py-2.5 text-sm font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-gray-700 hover:bg-slate-200 dark:hover:bg-gray-600 rounded-xl transition-colors min-h-[44px]"
            >
              Annulla
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2.5 text-sm font-medium text-white bg-yellow-500 hover:bg-yellow-600 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50 min-h-[44px]"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Salvataggio...' : 'Salva modifiche'}
            </button>
          </>
        ) : (
          <>
            <button
              onClick={onClose}
              className="px-4 py-2.5 text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 rounded-xl transition-colors flex items-center justify-center gap-2 min-h-[44px]"
            >
              <ChevronUp className="w-4 h-4" />
              Chiudi
            </button>
            <button
              onClick={() => setIsEditing(true)}
              className="px-5 py-2.5 text-sm font-medium text-slate-800 bg-gradient-to-b from-[#fff7a8] to-[#fdf751] hover:from-[#fdf751] hover:to-[#f5ef00] rounded-xl transition-all flex items-center justify-center gap-2 shadow-sm min-h-[44px]"
            >
              <Pencil className="w-4 h-4" />
              Suggerisci modifica
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ClientContentEditor;
